import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { brandColors } from "../../App";
import img from "../../asset/KALAMATA_LOGO_LIGHT.svg";
import imgDark from "../../asset/KALAMATA_LOGO_DARK.svg";
import { RootState } from "../../store";
import { logout } from "../../store/slices/authSlice";
import { generateMenuByAccess } from "../../utils/menuBuilder";
import LoginModal from "../Login/Login";

const NavBar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector((state: RootState) => state.auth.user);

  const token = useSelector((state: RootState) => state.auth.token);

  const permissions = useSelector((state: RootState) => state.auth.permissions);

  const isAuthenticated = Boolean(token);

  const role = user?.role ?? "";

  const groupedRoutes = generateMenuByAccess(permissions, role);

  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [activeMenuGroup, setActiveMenuGroup] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [loginOpen, setLoginOpen] = useState(false);

  /* =====================================================
   * ROUTE HELPERS
   * ===================================================== */
  const isRouteActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const isGroupActive = (group: string) => {
    const routes = groupedRoutes[group] ?? [];

    return routes.some((route) => isRouteActive(route.path));
  };

  /* =====================================================
   * PROFILE MENU
   * ===================================================== */
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    handleProfileMenuClose();
  };

  /* =====================================================
   * DESKTOP GROUP MENU
   * ===================================================== */
  const handleGroupMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    group: string,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuGroup(group);
  };

  const handleGroupMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMenuGroup(null);
  };

  const activeGroupRoutes = activeMenuGroup
    ? (groupedRoutes[activeMenuGroup] ?? [])
    : [];

  /* =====================================================
   * MOBILE DRAWER
   * ===================================================== */
  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handleDrawerNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  /* =====================================================
   * LOGO
   * ===================================================== */
  const logoElement = (
    <Box
      component="img"
      src={img}
      alt="Kalamata Logo"
      onClick={() => navigate("/")}
      sx={{
        width: 50,
        height: 50,
        objectFit: "contain",
        cursor: "pointer",
        display: "block",
      }}
    />
  );

  /* =====================================================
   * ACCOUNT ACTION
   * ===================================================== */
  const accountAction = isAuthenticated ? (
    <IconButton
      color="inherit"
      onClick={handleProfileMenuOpen}
      aria-label="Open account menu"
      sx={{
        p: 1,
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: "#FFFFFF",
          color: brandColors.primary,
          fontSize: 17,
          fontWeight: 600,
          border: "1px solid rgba(255,255,255,0.7)",
        }}
      >
        {user?.first_name?.[0]?.toUpperCase() || "U"}
      </Avatar>
    </IconButton>
  ) : (
    <Button
      color="inherit"
      onClick={() => setLoginOpen(true)}
      sx={{
        border: "1px solid rgba(255,255,255,0.7)",
        color: "#FFFFFF",
        textTransform: "none",
        minWidth: 64,
        "&:hover": {
          borderColor: "#FFFFFF",
          backgroundColor: "rgba(255,255,255,0.08)",
        },
      }}
    >
      Login
    </Button>
  );

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          height: "8vh",
          backgroundColor: brandColors.primary,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        }}
      >
        <Toolbar
          sx={{
            height: "100%",
            minHeight: "100% !important",
            px: {
              xs: 1.5,
              sm: 3,
            },
          }}
        >
          {/* =============================================
           * MOBILE HEADER
           * ============================================= */}
          {isMobile ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={toggleDrawer(true)}
                  aria-label="Open navigation"
                >
                  <MenuIcon />
                </IconButton>

                {logoElement}
              </Box>

              {accountAction}
            </Box>
          ) : (
            /* =============================================
             * DESKTOP HEADER
             *
             * 1fr / auto / 1fr keeps navigation perfectly
             * centered regardless of logo/profile width.
             * ============================================= */
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
              }}
            >
              {/* LEFT */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                {logoElement}
              </Box>

              {/* CENTER NAVIGATION */}
              <Box
                component="nav"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                {Object.keys(groupedRoutes).map((group) => {
                  const active = isGroupActive(group);

                  const open =
                    activeMenuGroup === group && Boolean(menuAnchorEl);

                  return (
                    <Box
                      key={group}
                      onClick={(event) => handleGroupMenuOpen(event, group)}
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: 0.75,
                        px: 1.5,
                        py: 1,
                        cursor: "pointer",
                        borderRadius: 1,
                        color: "#FFFFFF",
                        opacity: active || open ? 1 : 0.82,
                        transition:
                          "background-color 0.2s ease, opacity 0.2s ease",
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: "rgba(255,255,255,0.08)",
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 12,
                          right: 12,
                          bottom: 1,
                          height: 2,
                          borderRadius: 2,
                          backgroundColor:
                            active || open ? "#FFFFFF" : "transparent",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: "inherit",
                          fontWeight: active || open ? 600 : 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {group}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* RIGHT */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {accountAction}
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* =================================================
       * LOGIN MODAL
       * ================================================= */}
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* =================================================
       * DESKTOP GROUP DROPDOWN
       * ================================================= */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && Boolean(activeMenuGroup)}
        onClose={handleGroupMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 190,
              mt: 0.75,
              borderRadius: 1.5,
              border: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
        MenuListProps={{
          sx: {
            py: 0.75,
          },
        }}
      >
        {activeGroupRoutes.map((route) => {
          const active = isRouteActive(route.path);

          return (
            <MenuItem
              key={route.path}
              selected={active}
              onClick={() => {
                navigate(route.path);
                handleGroupMenuClose();
              }}
              sx={{
                mx: 0.75,
                px: 1.5,
                borderRadius: 1,
                fontSize: 14,
                minHeight: 38,
                "&.Mui-selected": {
                  color: brandColors.primary,
                  backgroundColor: "rgba(0, 90, 35, 0.08)",
                  fontWeight: 600,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(0, 90, 35, 0.12)",
                },
                "&:hover": {
                  backgroundColor: "#F5F7F5",
                },
              }}
            >
              {route.label}
            </MenuItem>
          );
        })}
      </Menu>

      {/* =================================================
       * PROFILE DROPDOWN
       * ================================================= */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
              mt: 1,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Profile */}
        <Box
          sx={{
            px: 2,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            gap: 1.4,
          }}
        >
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: brandColors.primary,
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {user?.email?.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.first_name
                ? `${user.first_name} ${user.last_name || ""}`.trim()
                : user?.email}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.15,
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.email}
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                mt: 0.7,
                px: 1,
                py: 0.25,
                borderRadius: 10,
                bgcolor: "#F3F4F6",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#6B7280",
                }}
              >
                {user?.role}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Logout */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            mx: 0.75,
            my: 0.75,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            color: "#374151",
            gap: 1.2,

            "&:hover": {
              bgcolor: "#FEF2F2",
              color: "#DC2626",
            },
          }}
        >
          <LogoutRoundedIcon
            sx={{
              fontSize: 19,
            }}
          />

          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
            }}
          >
            Logout
          </Typography>
        </MenuItem>
      </Menu>
      {/* =================================================
       * MOBILE DRAWER
       * ================================================= */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 250,
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        <Box
          sx={{
            height: 74,
            px: 2,
            display: "flex",
            alignItems: "center",
            maxHeight: "9vh",
            borderBottom: "1px solid #E5E7EB",
            flexDirection: "row",
          }}
        >
          <Box
            component="img"
            src={imgDark}
            alt="Kalamata Logo"
            sx={{
              // width: "100%",
              height: 50,
              objectFit: "contain",
              p: 0.5,
              borderRadius: 1,
              backgroundColor: "white",
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: "#3d5d46",
              flexGrow: 1,
              paddingTop: 2,
            }}
          >
            Asadi Inc
          </Typography>
        </Box>

        <Box
          sx={{
            width: 250,
            maxHeight: "90vh",
            overflow: "auto",
          }}
          role="presentation"
        >
          <List
            disablePadding
            sx={{
              py: 1,
            }}
          >
            {Object.entries(groupedRoutes).map(([groupName, routes]) => (
              <React.Fragment key={groupName}>
                <ListSubheader
                  disableSticky
                  sx={{
                    px: 2,
                    py: 0.75,
                    lineHeight: 1.5,
                    color: "#9CA3AF",
                    backgroundColor: "#FFFFFF",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    borderBottom: "1px solid #F3F4F6",
                  }}
                >
                  {groupName}
                </ListSubheader>

                {routes.map((route) => {
                  const active = isRouteActive(route.path);

                  return (
                    <ListItemButton
                      key={route.path}
                      selected={active}
                      onClick={() => handleDrawerNavigate(route.path)}
                      sx={{
                        mx: 1,
                        my: 0.25,
                        px: 2,
                        minHeight: 42,
                        borderRadius: 1,
                        borderLeft: "3px solid transparent",
                        "&.Mui-selected": {
                          color: brandColors.primary,
                          backgroundColor: "rgba(0, 90, 35, 0.07)",
                          borderLeftColor: brandColors.primary,
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(0, 90, 35, 0.10)",
                        },
                        "&:hover": {
                          backgroundColor: "#F5F7F5",
                        },
                      }}
                    >
                      <ListItemText
                        primary={route.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: active ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;
